import { MINER_STATUSES_E } from "@constants/MinerStatuses";
import { MinerControlAction3 } from "@mobx/api/miner";
import { MinerHashEvent } from "@services/http/socket/responses";
import { EQUIPMENT_TYPE } from "@store/Equipments/actionTypes";
import createHist, { History } from "@utils/undoredo";
import { Point } from "framer-motion";
import {
  Connection,
  Edge,
  EdgeChange,
  NodeChange,
  NodePositionChange,
  XYPosition,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
} from "reactflow";
import { getGlobalIntl } from "../../../i18n/ProviderIntl";
import { IRack, IRackItemForRack3, TypePartialRackItem3, isIRack, isMinerItem3, toRackItem3 } from "../locations/types";
import {
  BuildingData3,
  ConsumerKind3,
  CreateConsumersResult3,
  DraggedElementData3,
  EditorData3,
  findRackSectionInRack3,
  getNodeTypeColor3,
  getRackSectionState3,
  isRackSectionData3,
  NodeComplexState3,
  NodeData3,
  NodeSignal3,
  NodeState3,
  NodeType3,
  PlaceID3,
  ShemeNode3,
  addArc3,
  editorNameFromID3,
  getCollectionState3,
  isBuildingData3,
  isStateOn3,
  removeAllArcs3,
  removeArc3,
  toRackSectionData3,
} from "./types";

export interface TurnOnInfo {
  miners: IRackItemForRack3[];
  nodes: Set<string>;
}

export interface OpsResult3<T = EditorData3> {
  data: T;
  changed: boolean;
}

export interface ChangeRackItemsOpsResult3 extends OpsResult3<EditorData3> {
  toTurnOn: TurnOnInfo;
}

interface PosChange {
  count: number;
  position: XYPosition;
  positionAbsolute?: XYPosition;
  latestPosition?: XYPosition;
  latestPositionAbsolute?: XYPosition;
}

export interface PosChanges {
  changes: { [changeID: string]: PosChange | undefined };
}

export const createHistory = () => createHist<EditorData3>(50);

const isSameValue = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export const calcNodeStateFromBuildingData = (rr: BuildingData3): NodeComplexState3 => ({
  on: rr.m_online ?? 0,
  off: rr.m_offline ?? 0,
});

export const userDataStatus = (userData: unknown): NodeState3 =>
  isMinerItem3(userData)
    ? userData.status
    : isIRack(userData)
    ? getCollectionState3(userData.rack_items)
    : isRackSectionData3(userData)
    ? getRackSectionState3(userData)
    : isBuildingData3(userData)
    ? calcNodeStateFromBuildingData(userData)
    : { on: 0, off: 0 };

export const recalcBuildingOnOff = (data: BuildingData3): BuildingData3 => {
  let on = 0;
  let off = 0;

  for (const m of data.miners) {
    const [dOn, dOff] = isStateOn3(m.status) ? [1, 0] : [0, 1];
    on += dOn;
    off += dOff;
  }

  return {
    ...data,
    m_online: on,
    m_offline: off,
  };
};

export const propagateSignal = (
  dt: EditorData3,
  signal: NodeSignal3,
  snid: string,
  order = 0,
  doConfirm?: boolean
): EditorData3 => {
  const desc = dt.tree.forth[snid];
  if (desc?.size) {
    const nodes = dt.nodes.filter((n) => desc.has(n.id));
    for (const n of nodes) {
      dt = propagateSignal(dt, signal, n.id, order + 1, doConfirm);
    }
  }
  const ndt = {
    ...dt,
    nodes: dt.nodes.map((node) =>
      node.id === snid
        ? {
            ...node,
            data: {
              ...node.data,
              needConfirm:
                doConfirm &&
                (node.data.consumer === "miner" ||
                  (node.data.consumer === "section" &&
                    isRackSectionData3(node.data.userData) &&
                    !!node.data.userData.miner))
                  ? node.data.state
                  : undefined,
              lastSignal: {
                ts: new Date().getTime(),
                signal,
                order,
                active: true,
              },
            } as NodeData3,
          }
        : node
    ),
  };
  return ndt;
};

export const backPropagateAll = (dt: EditorData3, kind: EQUIPMENT_TYPE): EditorData3 => {
  if (kind !== EQUIPMENT_TYPE.ELECTRICAL) return dt;
  const backTab: { [nodeID: string]: string | undefined } = {};
  const stateTab: { [nodeID: string]: NodeComplexState3 } = {};
  const visited: { [pairMark: string]: NodeComplexState3 } = {};

  for (const edge of dt.edges) {
    backTab[edge.target] = edge.source;
  }

  for (const node of dt.nodes) {
    if (node.data.consumer !== "miner" && node.data.consumer !== "section") {
      stateTab[node.id] = { on: 0, off: 0 };
    }
  }

  const backProp = (nodeID: string, state: NodeComplexState3) => {
    const nodeBackID = backTab[nodeID];
    if (!nodeBackID) return;
    const pairMark = `${nodeID}_${nodeBackID}`;
    const visitedState = visited[pairMark];
    const prevStateValue = stateTab[nodeBackID];
    let newState = state;
    if (prevStateValue !== undefined) {
      newState = {
        on: prevStateValue.on - (visitedState?.on ?? 0) + state.on,
        off: prevStateValue.off - (visitedState?.off ?? 0) + state.off,
      };
    }
    stateTab[nodeBackID] = newState;
    visited[pairMark] = newState;
    backProp(nodeBackID, stateTab[nodeBackID]);
  };

  for (const node of dt.nodes) {
    let state: NodeComplexState3 | undefined = undefined;
    switch (node.data.consumer) {
      case "rack": {
        const miners = (node.data.userData as IRack).rack_items;
        state = getCollectionState3(miners);
        stateTab[node.id] = state;
        break;
      }
      case "building": {
        const miners = (node.data.userData as BuildingData3).miners;
        state = getCollectionState3(miners);
        stateTab[node.id] = state;
        break;
      }
      case "section": {
        const section = node.data.userData;
        if (isRackSectionData3(section) && section.miner) {
          const on = isStateOn3(section.miner.status);
          state = { on: +on, off: +!on };
        } else {
          state = { on: 0, off: 0 };
        }
        break;
      }
      case "miner": {
        const on = isStateOn3((node.data.userData as IRackItemForRack3).status);
        state = { on: +on, off: +!on };
        break;
      }
      default:
        state = undefined;
    }

    if (state) backProp(node.id, state);
  }

  return {
    ...dt,
    nodes: dt.nodes.map((node) => {
      const st = stateTab[node.id];
      if (!st) return node;
      return {
        ...node,
        data: {
          ...node.data,
          state: st,
        },
      };
    }),
  };
};

export type RacksChangesTable = { [k in string]: IRack };
export type MinersChangesTable = { [k in number]: IRackItemForRack3 };

export interface EditorOps {
  addEdge: (edge: Edge<any> | Connection) => EditorData3;
  addNode2: (
    type: NodeType3,
    position: XYPosition,
    dtt?: EditorData3,
    userData?: unknown,
    name?: string,
    refID?: { unref: number },
    consumer?: ConsumerKind3
  ) => EditorData3;
  changeNodes: (changes: NodeChange[]) => EditorData3;
  changeEdges: (changes: EdgeChange[]) => EditorData3;
  sendSignal: (sNodeID: string, signal: NodeSignal3) => EditorData3;
  clear: (empty: EditorData3) => EditorData3;
  undo: () => EditorData3;
  redo: () => EditorData3;
  reload: (d: EditorData3) => EditorData3;
  removeEdge: (nodeID: number) => EditorData3;
  removeNode: (nodeID: number | string) => EditorData3;
  setNodeName: (nodeID: number, name: string) => EditorData3;
  editNodeInfo: (sNodeID: string, name: string, notes: string, color: string) => EditorData3;
  createConsumers: (items: DraggedElementData3[], point: Point) => CreateConsumersResult3;
  mergeRacks: (rtab: RacksChangesTable) => OpsResult3;
  removeRacks: (rackIDs: Set<number>) => OpsResult3;
  changeRackItems: (
    rackID: number,
    items: TypePartialRackItem3[],
    mtab: MinersChangesTable
  ) => ChangeRackItemsOpsResult3;
  reportMinersControl: (control: MinerControlAction3, nds: Set<string>, success?: boolean) => EditorData3;
  changeMinerState: (ev: MinerHashEvent) => EditorData3;
  undoLength: number;
  redoLength: number;
}

export const createDataOps = (params: {
  placeID: PlaceID3;
  data: EditorData3;
  kind: EQUIPMENT_TYPE;
  history: History<EditorData3>;
  posChanges: PosChanges;
  nextColorIndex: () => number;
}): EditorOps => {
  const { placeID, data, kind, history, posChanges } = params;
  const hist = history;
  const { formatMessage } = getGlobalIntl();
  const rackConsumerName = (data: { rack_id: string; rackDisplayName?: string }) =>
    data.rackDisplayName ?? data.rack_id;
  const sectionRackName = (data: { rackName: string; rackDisplayName?: string }) =>
    data.rackDisplayName ?? data.rackName;

  const addEdge_ = (
    edge: Edge<any> | Connection,
    dta: EditorData3 = data
  ): EditorData3 => {
    const oldIDs = new Set(dta.edges.map((ed) => ed.id));
    const newEdges = [...addEdge(edge, dta.edges)];
    const newIDs = new Set(newEdges.filter((ed) => !oldIDs.has(ed.id)).map((ed) => ed.id));

    const Redo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      edges: newEdges,
      tree: addArc3(dt.tree, edge.source!, edge.target!),
    });

    const Undo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      edges: dt.edges.filter((ed) => !newIDs.has(ed.id)),
      tree: removeArc3(dt.tree, edge.target!),
    });

    const dt = backPropagateAll(hist.add({ Redo, Undo }, dta), kind);
    return dt;
  };

  const addNode = (
    type: NodeType3,
    nodeID: number,
    counter: number,
    node: ShemeNode3,
    dtt = data
  ): EditorData3 => {
    const newID = node.id;
    const wasLastID = dtt.lastNodeID;
    const wasCounter = dtt.nodesCounter;

    const Redo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      lastNodeID: nodeID,
      nodesCounter: {
        ...dt.nodesCounter,
        [type]: counter,
      },
      nodes: [...dt.nodes, node],
    });

    const Undo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      lastNodeID: wasLastID,
      nodesCounter: wasCounter,
      nodes: dt.nodes.filter((node) => node.id !== newID),
    });

    return hist.add({ Redo, Undo }, dtt);
  };

  const addNode2 = (
    type: NodeType3,
    position: XYPosition,
    dtt = data,
    userData?: unknown,
    name?: string,
    refID?: { unref: number },
    consumer?: ConsumerKind3
  ): EditorData3 => {
    const typeName = formatMessage({ id: `location.diagram.type_${type}` });
    const nodeID = dtt.lastNodeID + 1;
    if (refID) refID.unref = nodeID;
    const nodesCounter = dtt.nodesCounter[type] + 1;
    const newNode: ShemeNode3 = {
      id: `#${nodeID}`,
      type,
      position,
      data: {
        kind: type === "consumer" ? EQUIPMENT_TYPE.ELECTRICAL : kind,
        shemeKind: kind,
        ID: nodeID,
        name: name ?? `${typeName} ${nodesCounter}`,
        type,
        state: userDataStatus(userData),
        placeID: placeID,
        color: getNodeTypeColor3(type),
        userData,
        consumer: type === "consumer" ? consumer : undefined,
      },
    };

    return addNode(type, nodeID, nodesCounter, newNode, dtt);
  };

  const setNodeName = (nodeID: number, name: string): EditorData3 => {
    const sNodeID = `#${nodeID}`;
    const wasName = data.nodes.find((n) => n.id === sNodeID)?.data.name;
    if (wasName === undefined) return data;

    const Redo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      nodes: dt.nodes.map((n) => (n.id === sNodeID ? { ...n, data: { ...n.data, name } } : n)),
    });

    const Undo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      nodes: dt.nodes.map((n) =>
        n.id === sNodeID ? { ...n, data: { ...n.data, name: wasName } } : n
      ),
    });

    return hist.add({ Redo, Undo }, data);
  };

  const editNodeInfo = (sNodeID: string, name: string, notes: string, color: string): EditorData3 => {
    const wasNode = data.nodes.find((n) => n.id === sNodeID)?.data;
    const wasName = wasNode?.name;
    if (wasName === undefined) return data;

    const wasColor = wasNode?.color;
    const wasInfo = wasNode?.info;

    const Redo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      nodes: dt.nodes.map((n) =>
        n.id === sNodeID ? { ...n, data: { ...n.data, name, color, info: notes } } : n
      ),
    });

    const Undo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      nodes: dt.nodes.map((n) =>
        n.id === sNodeID
          ? {
              ...n,
              data: {
                ...n.data,
                name: wasName,
                color: wasColor,
                info: wasInfo,
              },
            }
          : n
      ),
    });

    return hist.add({ Redo, Undo }, data);
  };

  const removeNode = (nodeID: number | string): EditorData3 => {
    const sNodeID = typeof nodeID === "number" ? `#${nodeID}` : nodeID;

    const wasNode = data.nodes.find((n) => n.id === sNodeID);
    if (!wasNode) return data;

    const wasEdges = data.edges.filter((ed) => ed.target === sNodeID || ed.source === sNodeID);
    const newNodes = data.nodes.filter((n) => n.id !== sNodeID);
    const newEdges = data.edges.filter((ed) => ed.target !== sNodeID && ed.source !== sNodeID);

    const wasTree = Object.fromEntries(
      Object.entries(data.tree.forth).filter(([from, to]) => from === sNodeID || to?.has(sNodeID))
    );

    const Redo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      nodes: newNodes,
      edges: newEdges,
      tree: removeAllArcs3(dt.tree, sNodeID),
    });

    const Undo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      nodes: [...dt.nodes, { ...wasNode }],
      edges: [...dt.edges, ...wasEdges],
      tree: {
        forth: { ...dt.tree.forth, ...wasTree },
      },
    });

    return backPropagateAll(hist.add({ Redo, Undo }, data), kind);
  };

  const removeEdge = (nodeID: number, dtt = data): EditorData3 => {
    const sNodeID = `#${nodeID}`;

    const wasEdges = dtt.edges.filter((ed) => ed.target === sNodeID);
    if (!wasEdges.length) return dtt;

    const newEdges = dtt.edges.filter((ed) => ed.target !== sNodeID);

    const Redo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      edges: newEdges,
      tree: removeArc3(dt.tree, sNodeID),
    });

    const Undo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      edges: [...dt.edges, ...wasEdges],
      tree: addArc3(dt.tree, wasEdges[0].source, sNodeID),
    });

    return backPropagateAll(hist.add({ Redo, Undo }, dtt), kind);
  };

  const doChangeNodes_ = (dt: EditorData3, changes: NodeChange[]): EditorData3 => ({
    ...dt,
    nodes: [...applyNodeChanges(changes, dt.nodes)] as ShemeNode3[],
  });

  const changeNodesPosition = (change: NodePositionChange): EditorData3 => {
    const stop = change.dragging === false;
    const node = data.nodes.find((node) => node.id === change.id);
    if (node === undefined) return data;
    const editorName = editorNameFromID3(kind, placeID) ?? `${placeID}`;
    const changeID = `${editorName}:${change.id}`;
    const prev = posChanges.changes[changeID];
    if (!stop && (prev?.count ?? 0) < 100) {
      if (prev === undefined || prev.count === 0) {
        posChanges.changes[changeID] = {
          count: 1,
          position: node.position,
          positionAbsolute: node.positionAbsolute,
          latestPosition: change.position ?? node.position,
          latestPositionAbsolute: change.positionAbsolute ?? node.positionAbsolute,
        };
      } else {
        posChanges.changes[changeID]!.count = (prev?.count ?? 0) + 1;
        posChanges.changes[changeID]!.latestPosition = change.position ?? prev.position;
        posChanges.changes[changeID]!.latestPositionAbsolute =
          change.positionAbsolute ?? prev.positionAbsolute;
      }
      return doChangeNodes_(data, [change]);
    }

    if (prev === undefined) return data;

    posChanges.changes[changeID]!.count = 0;
    const nodeID = change.id;
    const { position: prevPos, positionAbsolute: prevAbsPos } = prev!;
    const nextPos = prev.latestPosition ?? change.position ?? node.position;
    const nextAbsPos =
      prev.latestPositionAbsolute ?? change.positionAbsolute ?? node.positionAbsolute;
    const Redo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      nodes: dt.nodes.map((node) =>
        node.id === nodeID
          ? {
              ...node,
              position: nextPos,
              positionAbsolute: nextAbsPos,
            }
          : node
      ),
    });
    const Undo = (dt: EditorData3): EditorData3 => ({
      ...dt,
      nodes: dt.nodes.map((node) =>
        node.id === nodeID
          ? { ...node, position: prevPos, positionAbsolute: prevAbsPos }
          : node
      ),
    });
    const result = hist.add({ Redo, Undo }, data);
    return result;
  };

  const changeNodes = (changes: NodeChange[]): EditorData3 => {
    if (changes[0]?.type === "position") {
      return changeNodesPosition(changes[0]);
    }
    return {
      ...data,
      nodes: [...applyNodeChanges(changes, data.nodes)] as ShemeNode3[],
    };
  };

  const changeEdges = (changes: EdgeChange[]): EditorData3 => ({
    ...data,
    edges: [...applyEdgeChanges(changes, data.edges)],
  });

  const sendSignal = (sNodeID: string, signal: NodeSignal3): EditorData3 =>
    propagateSignal(data, signal, sNodeID, 0, true);

  const clear = (empty: EditorData3): EditorData3 => {
    const prevData = { ...data };

    const Redo = (dt: EditorData3): EditorData3 => ({
      ...empty,
      lastNodeID: dt.lastNodeID,
      nodesCounter: dt.nodesCounter,
    });

    const Undo = (dt: EditorData3): EditorData3 => prevData;

    return hist.add({ Redo, Undo }, data);
  };

  const createConsumers = (items: DraggedElementData3[], point: Point): CreateConsumersResult3 => {
    let dt = { ...data };
    let { x, y } = point;
    const createdNodeIDs: string[] = [];
    const duplicateNodeIDs: string[] = [];

    const findNodesByMinerID = (nodes: ShemeNode3[], minerID: number) =>
      nodes.filter((n) => {
        if (n.type !== "consumer") return false;
        const userData = n.data.userData;
        if (isMinerItem3(userData)) return userData.miner_id === minerID;
        if (isRackSectionData3(userData)) return userData.miner?.miner_id === minerID;
        if (isIRack(userData)) {
          return userData.rack_items.some((item) => isMinerItem3(item) && item.miner_id === minerID);
        }
        if (isBuildingData3(userData)) {
          return userData.miners.some((item) => isMinerItem3(item) && item.miner_id === minerID);
        }
        return false;
      });

    const findDuplicateConsumers = (nodes: ShemeNode3[], userData: unknown): ShemeNode3[] => {
      const duplicates = new Map<string, ShemeNode3>();
      const addMatches = (matches: ShemeNode3[]) => {
        for (const match of matches) {
          duplicates.set(match.id, match);
        }
      };

      if (isMinerItem3(userData)) {
        addMatches(findNodesByMinerID(nodes, userData.miner_id));
      } else if (isRackSectionData3(userData)) {
        addMatches(
          nodes.filter(
            (n) => n.type === "consumer" && isRackSectionData3(n.data.userData) && n.data.userData.sectionKey === userData.sectionKey
          )
        );
        if (kind === EQUIPMENT_TYPE.ELECTRICAL && userData.miner?.miner_id !== undefined) {
          addMatches(findNodesByMinerID(nodes, userData.miner.miner_id));
        }
      } else if (isIRack(userData)) {
        addMatches(
          nodes.filter((n) => n.type === "consumer" && isIRack(n.data.userData) && n.data.userData.id === userData.id)
        );
        for (const item of userData.rack_items) {
          if (isMinerItem3(item)) addMatches(findNodesByMinerID(nodes, item.miner_id));
        }
      } else if (isBuildingData3(userData)) {
        addMatches(
          nodes.filter(
            (n) => n.type === "consumer" && isBuildingData3(n.data.userData) && n.data.userData.id === userData.id
          )
        );
        for (const item of userData.miners) {
          if (isMinerItem3(item)) addMatches(findNodesByMinerID(nodes, item.miner_id));
        }
      }

      return [...duplicates.values()];
    };

    for (const item of items) {
      x += 100;
      y += 100;
      const idRef = { unref: 0 };
      const idata = item.data;

      if (kind === EQUIPMENT_TYPE.ELECTRICAL && isMinerItem3(idata)) {
        continue;
      }

      const duplicateConsumers = findDuplicateConsumers(dt.nodes, idata);

      const name = isMinerItem3(idata)
        ? idata.miner_name
        : isRackSectionData3(idata)
        ? `${formatMessage({ id: "location.diagram.section" })} ${sectionRackName(idata)}-A${idata.y}${idata.x}`
        : isIRack(idata)
        ? `${formatMessage({ id: "location.diagram.rack" })} ${rackConsumerName(idata)}`
        : isBuildingData3(idata)
        ? `${formatMessage({ id: "location.diagram.building" })} ${idata.name}`
        : `${formatMessage({ id: "location.diagram.section" })} #${item.id}`;

      if (duplicateConsumers.length) {
        for (const duplicateConsumer of duplicateConsumers) {
          if (!duplicateNodeIDs.includes(duplicateConsumer.id)) {
            duplicateNodeIDs.push(duplicateConsumer.id);
          }
        }
        continue;
      }

      dt = addNode2("consumer", { x, y }, dt, idata, name, idRef, item.kind);
      createdNodeIDs.push(`#${idRef.unref}`);
    }
    return {
      data: dt,
      meta: {
        createdNodeIDs,
        duplicateNodeIDs,
      },
    };
  };

  const undo = (): EditorData3 => hist.undo(data);
  const redo = (): EditorData3 => hist.redo(data);
  const reload = (d: EditorData3): EditorData3 => {
    hist.clean();
    return { ...d };
  };

  const mergeRacks = (rtab: RacksChangesTable): OpsResult3 => {
    const nodesToDelete = new Set<string>();
    const minersToDelete = new Set<number>();
    let changed = false;

    let nodes = data.nodes
      .map((n) => {
        if (n.type !== "consumer") return n;
        const idata = n.data.userData;
        if (isIRack(idata)) {
          if (!Object.hasOwn(rtab, idata.id)) {
            // ConnectionsEditor3 is location-scoped, but rack updates currently arrive
            // only from the active building. Missing rack ids here are not a reliable
            // deletion signal, so keep existing nodes until we have explicit removal data.
            return n;
          }
          const newn = {
            ...n,
            data: {
              ...n.data,
              userData: {
                ...rtab[idata.id],
                rackDisplayName: rackConsumerName(idata),
              },
            },
          };
          if (!isSameValue(n.data.userData, newn.data.userData)) changed = true;
          for (const r of idata.rack_items) {
            if (isMinerItem3(r)) minersToDelete.add(r.miner_id);
          }
          return newn;
        } else if (isRackSectionData3(idata)) {
          const rack = rtab[idata.rackID];
          if (!rack) return n;
          const item = findRackSectionInRack3(rack, idata);
          if (!item) return n;
          const newData = toRackSectionData3(rack, item, idata.rackDisplayName);
          const newState = getRackSectionState3(newData);
          if (!isSameValue(idata, newData) || !isSameValue(n.data.state, newState)) changed = true;
          return {
            ...n,
            data: {
              ...n.data,
              userData: newData,
              state: newState,
            },
          };
        }
        return n;
      })
      .map((n) => {
        if (n.type !== "consumer") return n;
        const idata = n.data.userData;
        if (isMinerItem3(idata) && minersToDelete.has(idata.miner_id)) {
          nodesToDelete.add(n.id);
          changed = true;
        } else if (isBuildingData3(idata)) {
          if (!minersToDelete.size) return n;
          const nextMiners = idata.miners.filter((m) => !minersToDelete.has(m.miner_id));
          const newData = recalcBuildingOnOff({ ...idata, miners: nextMiners });
          const newState = calcNodeStateFromBuildingData(newData);
          if (!isSameValue(idata, newData) || !isSameValue(n.data.state, newState)) changed = true;
          return {
            ...n,
            data: {
              ...n.data,
              state: newState,
              userData: newData,
            },
          };
        }
        return n;
      });

    nodes = nodes.filter((n) => !nodesToDelete.has(n.id));
    const edges = data.edges.filter(
      (e) => !(nodesToDelete.has(e.target) || nodesToDelete.has(e.source))
    );
    if (edges.length !== data.edges.length) changed = true;
    return { data: backPropagateAll({ ...data, nodes, edges }, kind), changed };
  };

  const removeRacks = (rackIDs: Set<number>): OpsResult3 => {
    if (!rackIDs.size) return { data, changed: false };

    const nodesToDelete = new Set<string>();
    const minersToDelete = new Set<number>();
    let changed = false;

    let nodes = data.nodes
      .map((n) => {
        if (n.type !== "consumer") return n;
        const idata = n.data.userData;
        if (isIRack(idata) && rackIDs.has(idata.id)) {
          nodesToDelete.add(n.id);
          changed = true;
          for (const item of idata.rack_items) {
            if (isMinerItem3(item)) minersToDelete.add(item.miner_id);
          }
        }
        if (isRackSectionData3(idata) && rackIDs.has(idata.rackID)) {
          nodesToDelete.add(n.id);
          changed = true;
        }
        return n;
      })
      .map((n) => {
        if (n.type !== "consumer") return n;
        const idata = n.data.userData;
        if (isMinerItem3(idata) && minersToDelete.has(idata.miner_id)) {
          nodesToDelete.add(n.id);
          changed = true;
        } else if (isBuildingData3(idata)) {
          if (!minersToDelete.size) return n;
          const nextMiners = idata.miners.filter((m) => !minersToDelete.has(m.miner_id));
          const newData = recalcBuildingOnOff({ ...idata, miners: nextMiners });
          const newState = calcNodeStateFromBuildingData(newData);
          if (!isSameValue(idata, newData) || !isSameValue(n.data.state, newState)) changed = true;
          return {
            ...n,
            data: {
              ...n.data,
              state: newState,
              userData: newData,
            },
          };
        }
        return n;
      });

    nodes = nodes.filter((n) => !nodesToDelete.has(n.id));
    const edges = data.edges.filter(
      (e) => !(nodesToDelete.has(e.target) || nodesToDelete.has(e.source))
    );
    if (edges.length !== data.edges.length) changed = true;
    let tree = data.tree;
    for (const nodeID of nodesToDelete) {
      tree = removeAllArcs3(tree, nodeID);
    }

    return { data: backPropagateAll({ ...data, nodes, edges, tree }, kind), changed };
  };

  const changeRackItems = (
    rackID: number,
    items: TypePartialRackItem3[],
    mtab: MinersChangesTable
  ): ChangeRackItemsOpsResult3 => {
    const toDelete = new Set<string>();
    const toTurnOn: TurnOnInfo = { miners: [], nodes: new Set() };
    const hasMinerChanges = Object.keys(mtab).length > 0;
    let changed = false;
    const nodes = data.nodes.map((n) => {
      if (n.data.consumer === "rack" && (n.data.userData as IRack).id === rackID) {
        const rackData = n.data.userData as IRack;
        if (isStateOn3(n.data.state ?? { on: 0, off: 0 })) {
          const mt: MinersChangesTable = {};
          for (const item of rackData.rack_items) {
            if (isMinerItem3(item)) mt[item.miner_id] = { ...toRackItem3(item), rackID };
          }
          for (const item of items) {
            if (
              isMinerItem3(item) &&
              !Object.hasOwn(mt, item.miner_id) &&
              item.status === MINER_STATUSES_E.OFF
            ) {
              toTurnOn.miners.push({ ...toRackItem3(item), rackID });
              toTurnOn.nodes.add(n.id);
            }
          }
        }
        const newUserData = {
          ...(n.data.userData as IRack),
          rack_items: items,
        } as IRack;
        if (!isSameValue(n.data.userData, newUserData)) changed = true;
        return {
          ...n,
          data: {
            ...n.data,
            userData: newUserData,
          },
        };
      } else if (n.data.consumer === "section") {
        const sectionData = n.data.userData;
        if (isRackSectionData3(sectionData) && sectionData.rackID === rackID) {
          const nextItem = items.find((item) => item.x === sectionData.x && item.y === sectionData.y);
          if (!nextItem) {
            toDelete.add(n.id);
            changed = true;
            return n;
          }
          const nextData = toRackSectionData3(
            {
              id: rackID,
              rack_id: sectionData.rackName,
              rack_items: items,
              rack_type: [0, 0],
            } as IRack,
            nextItem,
            sectionData.rackDisplayName
          );
          const newState = getRackSectionState3(nextData);
          if (!isSameValue(sectionData, nextData) || !isSameValue(n.data.state, newState)) changed = true;
          return {
            ...n,
            data: {
              ...n.data,
              userData: nextData,
              state: newState,
            },
          };
        }
      } else if (n.data.consumer === "miner") {
        if ((n.data.userData as IRackItemForRack3).rackID === rackID) {
          const minerData = n.data.userData as IRackItemForRack3;
          if (!Object.hasOwn(mtab, minerData.miner_id)) {
            toDelete.add(n.id);
            changed = true;
          } else {
            if (!isSameValue(n.data.userData, mtab[minerData.miner_id])) changed = true;
            return {
              ...n,
              data: { ...n.data, userData: mtab[minerData.miner_id] },
            };
          }
        }
      } else if (n.data.consumer === "building") {
        if (!hasMinerChanges) return n;
        const idata = n.data.userData as BuildingData3;
        let hasBuildingChange = false;
        const nextData = {
          ...idata,
          miners: idata.miners.map((m) => {
            if (!Object.hasOwn(mtab, m.miner_id)) return m;
            if (!isSameValue(m, mtab[m.miner_id])) hasBuildingChange = true;
            return mtab[m.miner_id];
          }),
        };
        if (!hasBuildingChange) return n;
        const newData = recalcBuildingOnOff(nextData);
        const newState = calcNodeStateFromBuildingData(newData);
        changed = true;
        return {
          ...n,
          data: {
            ...n.data,
            userData: newData,
            state: newState,
          },
        };
      }
      return n;
    });
    const edges = data.edges.filter((e) => !(toDelete.has(e.target) || toDelete.has(e.source)));
    if (edges.length !== data.edges.length) changed = true;
    const dt = backPropagateAll(
      { ...data, nodes: nodes.filter((n) => !toDelete.has(n.id)), edges },
      kind
    );
    return { data: dt, toTurnOn, changed };
  };

  const changeMinerState = (ev: MinerHashEvent): EditorData3 => {
    let info = {
      foundNode: undefined as string | undefined,
      oldState: { on: 0, off: 0 } as NodeComplexState3,
      newState: { on: 0, off: 0 } as NodeComplexState3,
    };

    const changeStatus = (id: string) => {
      info.foundNode = id;
      return ev.state;
    };

    const dt: EditorData3 = {
      ...data,
      nodes: data.nodes.map((n) => {
        switch (n.data.consumer) {
          case "miner": {
            const m = n.data.userData as IRackItemForRack3;
            if (m.miner_id === +ev.id!) {
              if (isStateOn3(m.status)) {
                info.oldState = { on: 1, off: 0 };
                info.newState = { on: 0, off: 1 };
              }
              return {
                ...n,
                data: {
                  ...n.data,
                  state: ev.state,
                  userData: { ...m, status: changeStatus(n.id) },
                },
              };
            }
            return n;
          }
          case "rack": {
            const r = n.data.userData as IRack;
            const rr: IRack = {
              ...r,
              rack_items: r.rack_items.map((item) =>
                isMinerItem3(item) && item.miner_id === +ev.id!
                  ? { ...item, status: changeStatus(n.id) }
                  : item
              ),
            };
            const nn = {
              ...n,
              data: { ...n.data, userData: rr },
            };
            info.oldState = nn.data.state as NodeComplexState3;
            const newState = { on: 0, off: 0 };
            for (const item of rr.rack_items) {
              if (!isMinerItem3(item)) continue;
              if (isStateOn3(item.status)) newState.on++;
              else newState.off++;
            }
            info.newState = newState;
            nn.data.state = info.newState;
            return nn;
          }
          case "section": {
            const section = n.data.userData;
            if (isRackSectionData3(section) && section.miner?.miner_id === +ev.id!) {
              const nextMiner = { ...section.miner, status: changeStatus(n.id) };
              return {
                ...n,
                data: {
                  ...n.data,
                  state: nextMiner.status,
                  userData: {
                    ...section,
                    miner: nextMiner,
                  },
                },
              };
            }
            return n;
          }
          case "building": {
            const r = n.data.userData as BuildingData3;
            const rr: BuildingData3 = recalcBuildingOnOff({
              ...r,
              miners: r.miners.map((item) =>
                isMinerItem3(item) && item.miner_id === +ev.id!
                  ? { ...item, status: changeStatus(n.id) }
                  : item
              ),
            });
            const nn = {
              ...n,
              data: { ...n.data, userData: rr },
            };
            info.oldState = nn.data.state as NodeComplexState3;
            const newState = calcNodeStateFromBuildingData(rr);
            info.newState = newState;
            nn.data.state = info.newState;
            return nn;
          }
          default:
            return n;
        }
      }),
    };

    if (info.foundNode) return backPropagateAll(dt, kind);
    return dt;
  };

  const reportMinersControl = (
    control: MinerControlAction3,
    nds: Set<string>,
    success = true
  ): EditorData3 => {
    const nodes = data.nodes.map((n) => {
      if (nds.has(n.id)) {
        const canRollback =
          n.data.consumer === "miner" ||
          (n.data.consumer === "section" &&
            isRackSectionData3(n.data.userData) &&
            n.data.userData.miner !== undefined);
        const nn = {
          ...n,
          data: {
            ...n.data,
            state:
              success || control === "restart" || !canRollback
                ? n.data.state
                : n.data.needConfirm,
            needConfirm: undefined,
            lastSignal: { ...n.data.lastSignal, active: false, ts: 0, success },
          } as NodeData3,
        };
        return nn;
      }
      return n;
    });
    return {
      ...data,
      nodes,
    };
  };

  return {
    addEdge: addEdge_,
    addNode2,
    changeNodes,
    changeEdges,
    sendSignal,
    clear,
    undo,
    redo,
    reload,
    removeEdge,
    removeNode,
    setNodeName,
    editNodeInfo,
    createConsumers,
    mergeRacks,
    removeRacks,
    changeRackItems,
    reportMinersControl,
    changeMinerState,

    get undoLength() {
      return hist.undoLength;
    },
    get redoLength() {
      return hist.redoLength;
    },
  };
};
